import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AuthTokenRES,
  FactorsRES,
  FirmRES,
  GetBffUserRES,
  GetFeatureFlagRES,
  GetTenantRES,
  SubscriptionRES,
  UserRES,
  UserTokenRES
} from '@smarsh-cyber/shared/api';
import { getBasePortal } from '@smarsh-cyber/shared/utils';
import { Observable } from 'rxjs';
import {
  AUTH_MIDDLEWARE_URL,
  BFF_BASE_URL,
  CHART_ALERT_SERVER_URL
} from '../../app.constants';
import {
  API,
  GET_ACTIVE_FIRM_API_PATH,
  GET_BFF_USERS_API_PATH,
  GET_FACTORS_API_PATH,
  GET_TENANT_ID,
  GET_USER_DETAILS_API_PATH,
  GET_USER_PERMISSIONS,
  GET_USER_TOKEN_API_PATH,
  SERVICE
} from '../utils/app-state.constants';
import { HEADER } from '@smarsh-cyber/shared/constants';

@Injectable({
  providedIn: 'root'
})
export class DataService {
  alertServerUrl = CHART_ALERT_SERVER_URL;
  headers: HttpHeaders;

  constructor(private http: HttpClient) {}

  getUserDetail(portalURL: string): Observable<UserRES> {
    return this.http.get<UserRES>(
      portalURL.concat(SERVICE) + GET_USER_DETAILS_API_PATH,
      {
        withCredentials: true
      }
    );
  }

  getUserToken(portalURL: string): Observable<UserTokenRES> {
    return this.http.post<UserTokenRES>(
      portalURL.concat(API) + GET_USER_TOKEN_API_PATH,
      JSON.stringify({ sName: 'PublicAPI' }),
      {
        withCredentials: true
      }
    );
  }

  //TODO: remove this method post feature flag implementation
  checkSubscription(
    portalURL: string,
    appliance_id: string | false,
    serviceName: string
  ): Observable<SubscriptionRES> {
    return this.http.post<SubscriptionRES>(
      portalURL.concat('/api/validate/checkServiceSubscription'),
      JSON.stringify({ firmId: appliance_id, serviceName }),
      {
        withCredentials: true
      }
    );
  }

  refreshAuthToken(
    refreshToken: string,
    portalName: string
  ): Observable<AuthTokenRES> {
    const url = `${AUTH_MIDDLEWARE_URL}/${portalName}`;
    return this.http.get<AuthTokenRES>(url, {
      headers: {
        'Refresh-Token': `Bearer ${refreshToken}`
      }
    });
  }

  getActiveFirms(tenantId: string, token: AuthTokenRES): Observable<FirmRES> {
    const url = `${BFF_BASE_URL}${GET_ACTIVE_FIRM_API_PATH}`;
    return this.http.get<FirmRES>(url, {
      headers: {
        [HEADER.TENANT_ID]: tenantId,
        [HEADER.AUTHORIZATION]: `Bearer ${token?.accessToken}`
      }
    });
  }

  getBffUser(tenantId: string, token: AuthTokenRES): Observable<GetBffUserRES> {
    const url = `${BFF_BASE_URL}${GET_BFF_USERS_API_PATH}`;
    return this.http.get<GetBffUserRES>(url, {
      headers: {
        [HEADER.TENANT_ID]: tenantId,
        [HEADER.AUTHORIZATION]: `Bearer ${token?.accessToken}`
      }
    });
  }

  searchBffUser(searchKey: string): Observable<GetBffUserRES> {
    const url = `${BFF_BASE_URL}${GET_BFF_USERS_API_PATH}`;
    // const url = 'http://localhost:1500/api/v2/user';
    return this.http.get<GetBffUserRES>(url, {
      params: {
        username: searchKey
      }
    });
  }

  getFactors(): Observable<FactorsRES> {
    const url = `${BFF_BASE_URL}${GET_FACTORS_API_PATH}`;
    return this.http.get<FactorsRES>(url);
  }

  getTenantDetails(
    portalName: string,
    token: AuthTokenRES
  ): Observable<GetTenantRES> {
    const url = `${BFF_BASE_URL}${GET_TENANT_ID}${portalName}`;
    return this.http.get<GetTenantRES>(url, {
      headers: {
        [HEADER.AUTHORIZATION]: `Bearer ${token?.accessToken}`
      }
    });
  }

  getAuthTokenFromCode(
    code: string,
    portalName: string
  ): Observable<AuthTokenRES> {
    const url = `${AUTH_MIDDLEWARE_URL}/token`;

    return this.http.post<AuthTokenRES>(
      url,
      {
        code: code,
        client: getBasePortal(portalName),
        redirectUri: sessionStorage.getItem('redirectUri')
      },
      {
        headers: {
          'X-SubDomain': portalName
        }
      }
    );
  }

  getUserPermissionsPreAppInit(
    token: AuthTokenRES,
    tenantId: string,
    userId: string,
    buId: string | null
  ) {
    const businessUnitId = buId && buId !== 'null' ? buId : '';
    const headers = {
      [HEADER.AUTHORIZATION]: `Bearer ${token?.accessToken}`,
      [HEADER.TENANT_ID]: tenantId,
      [HEADER.USER_ID]: userId
    };
    if (businessUnitId) {
      headers[HEADER.BUID_ID] = businessUnitId;
    }
    const url = `${BFF_BASE_URL}${GET_USER_PERMISSIONS}`;
    // const url = 'http://localhost:1500/api/feature-permissions';
    return this.http.get<GetFeatureFlagRES>(url, {
      headers: headers
    });
  }

  getUserPermissions(userId: string, buId: string | null) {
    const businessUnitId = buId && buId !== 'null' ? buId : '';
    const headers = {
      [HEADER.BUID_ID]: businessUnitId,
      [HEADER.USER_ID]: userId
    };
    const url = `${BFF_BASE_URL}${GET_USER_PERMISSIONS}`;
    // const url = 'http://localhost:1500/api/feature-permissions';
    return this.http.get<GetFeatureFlagRES>(url, {
      headers: headers
    });
  }

  getLatestAppVersion() {
    const versionUrl = `assets/version.json?v=${new Date().getTime()}`; // use of date -> Cache buster technique
    return this.http.get<{ version: string }>(versionUrl, {
      headers: {
        cache: 'no-store'
      }
    });
  }
}
